import React, {Component} from "react";
import {Button, Dialog, Icon, Select, TextField} from '@vitalsource/vstui';
import {ProductDataItem, ProjectDataItem} from "../types/StudioAPITypes";
import {sortBy} from "../utils/sortCompUtils";
import {RouteComponentProps} from "react-router";
import "../../styles/ProductList.css";
import '../../styles/Layouts.css';
import FullscreenDialog from "./FullscreenDialog";
import AddProductWizard from "./addProductWizard/AddProductWizard";
import {ProductCardType, ProjectConfigType} from "../types/Types";
import ProductCardContainer from "../containers/ProductCardContainer";
import {checkTitleDupAndModify, scrollToObject} from "../utils/helperUtils";
import importIconAnim from "../../img/import.gif";
import createIconAnim from "../../img/create.gif";
import ToolbarUtility from "./ToolbarUtility";
import {FILE_EXTENSIONS, PRODUCT_FILTER_PREFIX,SORT_ATTRIBUTE} from "../utils/constants";
import EmptyItemsPlaceholder from "./EmptyItemsPlaceholder";
import PollingManager from "../utils/PollingManager";
import FileImportProgressIndicatorContainer from "../containers/FileImportProgressIndicatorContainer";

const STUDIO_PRODUCT_SORT_VALUE = "STUDIO_PRODUCT_SORT_VALUE";
const PAGE_CONTAINER_ID = "pageContainer";

interface RouteParams {
  projectId: string;
}

interface ProductsListState {
  addDialogVisible: boolean;
  sort: string;
  projectConfig: ProjectConfigType;
  productCreatedFromFileImport: string;
  disableDialogInteraction: boolean;
}

export interface ProductsListProps extends RouteComponentProps<RouteParams> {
  items: ProductDataItem[],
  projectId: string;
  loading: boolean;
  fetchData: (projectId: string) => Promise<void | {}>;
  updateProductsFilter: (e: any) => void;
  addProduct: (projectId: string, name: string) => Promise<void | {}>;
  importFile: (projectId: string, file: File)  => Promise<void | {}>;
  resetProducts: () => void;
  filter: string;
  isLoading: boolean;
}

export default class ProductsList extends Component<ProductsListProps, ProductsListState> {

  private addButton: React.RefObject<React.ReactNode>;
  private fileInputSelector: React.RefObject<HTMLInputElement>;

  constructor(props: ProductsListProps) {
    super(props);

    this.state = {
      addDialogVisible: false,
      sort: localStorage.getItem(STUDIO_PRODUCT_SORT_VALUE) || SORT_ATTRIBUTE.lastEdited.key,
      projectConfig: {
        allowPdfImport: false,
        allowXmlZipImport: false
      },
      productCreatedFromFileImport: "",
      disableDialogInteraction: false
    };

    this.addButton = React.createRef<React.ReactNode>();
    this.fileInputSelector = React.createRef<HTMLInputElement>();

    this.clickAdd = this.clickAdd.bind(this);
    this.addDialogRequestClose = this.addDialogRequestClose.bind(this);
    this.productTypeSelected = this.productTypeSelected.bind(this);
    this.handleInputFileSelectedChanged = this.handleInputFileSelectedChanged.bind(this);
    this.handleRetrySelectFile = this.handleRetrySelectFile.bind(this);
    this.onUpdateFilter = this.onUpdateFilter.bind(this);
    this.onUpdateSort = this.onUpdateSort.bind(this);
    this.setRefAddButton = this.setRefAddButton.bind(this);
    this.setProductCreatedFromFileImport = this.setProductCreatedFromFileImport.bind(this);
  }

  componentDidMount() {

    // If data in the model is for a different project, reset the model before requesting new data
    if (this.props.projectId !== this.props.match.params.projectId) {
      this.props.resetProducts();
    }

    this.props.fetchData(this.props.match.params.projectId).then((data: void | {}) => {
      if (data) {
        const projectData = data as ProjectDataItem;
        this.setState({
          projectConfig: {
            allowPdfImport: projectData.allowPdfImport,
            allowXmlZipImport: projectData.allowXmlZipImport
          }
        });
      }
    });
  }
  
  componentWillUnmount(): void {

    // Disable any polls for product data in this view
    this.props.items.forEach((item) => {
      PollingManager.remove(PollingManager.createKey(this.props.projectId, item.id));
    });
  }

  clickAdd(e: MouseEvent) {
    this.setState({
      addDialogVisible: true
    });
  }

  addDialogRequestClose() {
    this.setState({
      addDialogVisible: false
    });

    if (this.addButton) {
      const elem = document.querySelector<HTMLButtonElement>("#add-dialog-close");
      if (elem) {
        elem.focus();
      }
    }
  }

  applyFilterAndSortToProducts(products: any[], filter: string, sort: string) {
    let filteredProducts =  products.filter((product) => {
      return product.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1
    });

    return sortBy(sort, filteredProducts);
  };

  handleInputFileSelectedChanged(event: React.FormEvent<HTMLInputElement>) {
    if (event.currentTarget && event.currentTarget.files) {
      if (event.currentTarget.files.length === 1) {
        let fileToImport = event.currentTarget.files[0] as File;
        this.props.importFile(this.props.projectId, fileToImport)
          .then((productData: void | {}) => {
            const typedData = productData as ProjectDataItem;
            this.setProductCreatedFromFileImport(typedData.id);
            this.setState({
              disableDialogInteraction: false
            })
          }, (err ) => {
            this.setState({
              disableDialogInteraction: false
            });
          })
      }
    }
    this.setState({
      disableDialogInteraction: true
    });
  }

  handleRetrySelectFile() {
    this.setState({
      addDialogVisible: true,
    });
  }

  mapProductsToProductCard(products: any[]): any[] {
    let productContainerList = products.map( (product) => {
      return <ProductCardContainer
        key={product.id}
        id={product.id}
        name={product.name}
        coverUrl={product.coverUrl}
        modifiedDate={product.lastOpenDate}
        status={product.status}
        projectId={this.props.projectId}
        numberOfScreens={product.screens}
        publishDate={product.publishDate}
    />
    });

    if (productContainerList.length) {
      return productContainerList;
    } else {
      scrollToObject("toolbar", PAGE_CONTAINER_ID);
      return Array.apply(null, Array(8)).map( (i: any, j: number) => {
        return (
            <ProductCardContainer
              key={j}
              id=""
              name=""
              coverUrl=""
              modifiedDate=""
              status=""
              projectId=""
              numberOfScreens={0}
              publishDate=""
            />
        )
      });
    }
  }

  setProductCreatedFromFileImport(id: string) {
    this.setState({
      productCreatedFromFileImport: id
    });
  }

  productTypeSelected(type: ProductCardType) {

    const createUntitledName = () => {
      const titles = this.props.items.map((item) => item.name);
      return checkTitleDupAndModify("Untitled","", 0, titles);
    };

    switch (type) {
      case ProductCardType.EPUB:
        this.props.addProduct(this.props.projectId, createUntitledName())
          .then((productData: void | {}) => {
            this.addDialogRequestClose();
            if (productData) {
              const typedData = productData as ProjectDataItem;
              scrollToObject(typedData.id, PAGE_CONTAINER_ID);
            }
          });
        break;
      case ProductCardType.Import:
        if (this.fileInputSelector && this.fileInputSelector.current) {
          this.fileInputSelector.current.click();
        }
        break;
      default:
        // Do nothing
    }
  }

  onUpdateSort(e: any) {
      localStorage.setItem(STUDIO_PRODUCT_SORT_VALUE, e.currentTarget.value || "");
      this.setState({
        sort: e.currentTarget.value
      });
  }

  onUpdateFilter(e: any) {
    localStorage.setItem(PRODUCT_FILTER_PREFIX + this.props.projectId, e.currentTarget.value || "");
    this.props.updateProductsFilter(e)
  }

  setRefAddButton(ref: any) {
    this.addButton = ref;
  }

  getAcceptedFileFormats(): string {

    let fileExts = [FILE_EXTENSIONS.PDF];

    if (this.state.projectConfig.allowXmlZipImport) {
      fileExts.push(FILE_EXTENSIONS.ZIP);
    }
    return fileExts.toString();
  };

  render() {
    let filteredAndSortedProducts = this.applyFilterAndSortToProducts(this.props.items, this.props.filter, this.state.sort);
    let acceptableFileFormats = this.getAcceptedFileFormats();

    let dialog;
    if (this.state.addDialogVisible) {
      dialog = (
        <FullscreenDialog interactive={!this.state.disableDialogInteraction}
                          onRequestClose={this.addDialogRequestClose}>
          <input
            type="file"
            accept={acceptableFileFormats}
            style={{display: 'none'}}
            onChange={this.handleInputFileSelectedChanged}
            ref={this.fileInputSelector}
          />
          <FileImportProgressIndicatorContainer
                                      handleClose={ () => {
                                        this.addDialogRequestClose();
                                        scrollToObject(this.state.productCreatedFromFileImport, PAGE_CONTAINER_ID);
                                      }}
                                      handleUploadRetry={ () => {
                                        this.productTypeSelected(ProductCardType.Import);
                                      }}>
            <AddProductWizard
                            onProductTypeSelected={this.productTypeSelected}
                            projectConfig={this.state.projectConfig}/>
          </FileImportProgressIndicatorContainer>
        </FullscreenDialog>
      )
    }

    return (
      <div id={PAGE_CONTAINER_ID} className={filteredAndSortedProducts.length ? "page-container" : "page-container disablescroll"}>
        {/** Preload gifs for add product wizard */}
        <img src={importIconAnim} hidden={true}/>
        <img src={createIconAnim} hidden={true}/>
        {!this.props.isLoading && !this.props.items.length? <EmptyItemsPlaceholder buttonOnClick={this.clickAdd}/> :
          <div className="page-inner-container">
            <ToolbarUtility
                          onUpdateFilter={this.onUpdateFilter}
                          onUpdateSort={this.onUpdateSort}
                          filter={this.props.filter}
                          sort={this.state.sort}
                          btnAction={this.clickAdd}
                          addButtonRefCb={this.setRefAddButton}
                          emptyFilterState={!filteredAndSortedProducts.length && this.props.filter !== ""}/>
            <div className={"card-grid"}>
              {this.mapProductsToProductCard(filteredAndSortedProducts)}
            </div>
          </div>
        }
        {dialog}
      </div>
    )
  }
}
